import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import NaviCatalogLayout from "../components/NaviCatalogLayout";
import { useDestination, API } from "../App";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  ShoppingCart,
  Package,
  Truck,
  Zap,
  ArrowLeft,
  ExternalLink,
  Shield,
  CheckCircle2,
  BadgePercent,
  Info,
} from "lucide-react";
import VerifiedDeliveryBadge from "../components/VerifiedDeliveryBadge";
import LogisticsTransparency from "../components/LogisticsTransparency";
import { normalizeCatalogProduct } from "../lib/catalogProduct";
import { fetchLiveProduct } from "../services/liveCatalogApi";
import LuxuryProductImage from "../components/LuxuryProductImage";
import { CHEEP_LUXURY_PLACEHOLDER_DATA_URI, resolveListingImage } from "../lib/productImageAssets";
import {
  buildVariationGallery,
  findSelectionForImage,
  getDefaultVariationSelection,
  normalizeVariationMatrix,
  resolveVariationImage,
} from "../lib/variationImageLock";

const getMarketplaceInfo = (marketplace) => {
  const info = {
    amazon: { color: "bg-[#FF9900]", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/a/a9/Amazon_logo.svg/100px-Amazon_logo.svg.png", name: "Amazon" },
    alibaba: { color: "bg-[#FF6A00]", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/9/9a/Alibaba_Group_Logo.svg/100px-Alibaba_Group_Logo.svg.png", name: "Alibaba" },
    aliexpress: { color: "bg-[#E62E04]", logo: null, name: "AliExpress" },
    ebay: { color: "bg-[#0064D2]", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/1/1b/EBay_logo.svg/100px-EBay_logo.svg.png", name: "eBay" },
    temu: { color: "bg-[#FB7701]", logo: null, name: "TEMU" },
    shein: { color: "bg-black", logo: null, name: "SHEIN" },
  };
  return info[marketplace?.toLowerCase()] || { color: "bg-slate-600", logo: null, name: marketplace };
};

export default function ProductDetailPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { destination, currency, shippingSpeed, setShippingSpeed } = useDestination();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState({});

  useEffect(() => {
    setSelectedImage(0);
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [productId]);

  useEffect(() => {
    if (!product?.variation_matrix?.locked) {
      setSelectedOptions({});
      return;
    }
    setSelectedOptions(getDefaultVariationSelection(product.variation_matrix));
  }, [product]);

  useEffect(() => {
    fetchProduct();
    // fetchProduct closes over destination/currency; productId is the load key
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, destination, currency]);

  const fetchProduct = async () => {
    setLoading(true);
    try {
      // Prefer DHC live catalog API (pricing + fresh images from Supabase)
      try {
        const data = await fetchLiveProduct(API, productId, {
          destination,
          currency,
          shippingSpeed,
        });
        setProduct(normalizeCatalogProduct(data, { destination, currency }));
        return;
      } catch (apiError) {
        console.warn("[ProductDetail] DHC API miss, trying local catalog", apiError?.message);
      }

      // Fallback: bundled products.json (showcase catalog)
      const base = typeof window !== "undefined" ? window.location.origin : "";
      const res = await fetch(`${base}/products.json`, {
        headers: { "Cache-Control": "no-cache" },
      });
      if (res.ok) {
        const data = await res.json();
        const rows = Array.isArray(data)
          ? data
          : Array.isArray(data?.products)
            ? data.products
            : [];
        const found = rows.find((p) => String(p.product_id) === String(productId));
        if (found) {
          setProduct(normalizeCatalogProduct(found, { destination, currency }));
          return;
        }
      }

      setProduct(null);
    } catch (error) {
      console.error("Error fetching product:", error);
      toast.error("Failed to load product");
      setProduct(null);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async () => {
    try {
      await axios.post(
        `${API}/cart/add`,
        { product_id: productId, quantity },
        { withCredentials: true }
      );
      toast.success("Added to cart");
    } catch (error) {
      console.error("Error adding to cart:", error);
      toast.error("Failed to add to cart");
    }
  };

  const formatPrice = (price) => {
    const currencySymbols = {
      USD: "$",
      BBD: "BBD $",
      CAD: "CAD $",
      GBP: "£",
      TTD: "TTD $",
      JMD: "JMD $",
      EUR: "€",
    };
    return `${currencySymbols[currency] || "$"}${price.toFixed(2)}`;
  };

  if (loading) {
    return (
      <NaviCatalogLayout title="Product">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid md:grid-cols-2 gap-8">
            <Skeleton className="aspect-square" />
            <div className="space-y-4">
              <Skeleton className="h-8 w-3/4" />
              <Skeleton className="h-6 w-1/4" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-16 w-1/2" />
              <Skeleton className="h-12 w-full" />
            </div>
          </div>
        </div>
      </NaviCatalogLayout>
    );
  }

  if (!product) {
    return (
      <NaviCatalogLayout title="Product">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <Package className="h-16 w-16 mx-auto text-slate-300 mb-4" />
          <h2 className="text-xl font-semibold text-slate-900 mb-2">
            Product not found
          </h2>
          <Button onClick={() => navigate("/products")}>Browse Products</Button>
        </div>
      </NaviCatalogLayout>
    );
  }

  const standardPrice =
    product.pricing?.standard?.final_price || product.landed_price || 0;
  const expressPrice =
    product.pricing?.express?.final_price ||
    Math.round(standardPrice * 1.15 * 100) / 100;
  const variationMatrix = normalizeVariationMatrix(product?.variation_matrix);
  const lockedImage = resolveVariationImage(variationMatrix, selectedOptions);
  const variationGallery = buildVariationGallery(product, selectedOptions);
  const galleryImages = variationMatrix.locked
    ? variationGallery
    : product.is_verified_image && product.real_images?.length
      ? product.real_images
      : product.images?.filter((u) => u && !u.includes("unsplash.com")) || product.images || [];
  const activeImage = variationMatrix.locked
    ? lockedImage || variationGallery[0] || resolveListingImage(product)
    : galleryImages[selectedImage] || galleryImages[0] || resolveListingImage(product);

  return (
    <NaviCatalogLayout title="Product">
      <div
        id="product-detail-top"
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 scroll-mt-[148px]"
      >
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate("/products")}
          className="mb-6"
          data-testid="back-button"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Products
        </Button>

        <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
          {/* Images */}
          <div className="space-y-4">
            <div className="aspect-square bg-slate-100 rounded-sm overflow-hidden border border-slate-200">
              <LuxuryProductImage
                product={{
                  ...product,
                  images: activeImage ? [activeImage] : product.images,
                }}
                alt={product.name}
                className="w-full h-full object-contain bg-white p-4"
                testId="product-main-image"
              />
            </div>
            {galleryImages.length > 1 && (
              <div className="flex gap-2">
                {galleryImages.map((img, idx) => (
                  <button
                    key={img}
                    onClick={() => {
                      if (variationMatrix.locked) {
                        const selection = findSelectionForImage(variationMatrix, img);
                        if (selection) setSelectedOptions(selection);
                      } else {
                        setSelectedImage(idx);
                      }
                    }}
                    className={`w-16 h-16 rounded-sm border overflow-hidden ${
                      (variationMatrix.locked ? lockedImage === img : selectedImage === idx)
                        ? "border-orange-500"
                        : "border-slate-200"
                    }`}
                  >
                    <img
                      src={img}
                      alt={`${product.name} ${idx + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = CHEEP_LUXURY_PLACEHOLDER_DATA_URI;
                      }}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Details */}
          <div className="space-y-6">
            {/* Marketplace Badge with Logo */}
            <div className="flex items-center gap-2">
              {(() => {
                const mpInfo = getMarketplaceInfo(product.marketplace);
                return (
                  <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-white ${mpInfo.color}`}>
                    {mpInfo.logo && (
                      <img 
                        src={mpInfo.logo} 
                        alt="" 
                        className="h-5 w-auto brightness-0 invert"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    {mpInfo.name || product.marketplace.toUpperCase()}
                  </span>
                );
              })()}
              <Badge variant="secondary">{product.category}</Badge>
            </div>

            {/* Title */}
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-900" data-testid="product-title">
              {product.name}
            </h1>
            {product.product_sku && (
              <p className="text-xs font-mono text-slate-500 uppercase tracking-widest">
                SKU: {product.product_sku}
              </p>
            )}

            {/* Description */}
            <p className="text-slate-600">{product.description}</p>

            {variationMatrix.locked && variationMatrix.options.length > 0 && (
              <div className="space-y-4 border border-slate-200 rounded-lg p-4 bg-slate-50">
                <p className="text-sm font-semibold text-slate-900">Select your exact variation</p>
                {variationMatrix.options.map((option) => (
                  <div key={option.name} className="space-y-2">
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                      {option.name}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {option.values.map((value) => {
                        const isActive = selectedOptions[option.name] === value;
                        return (
                          <button
                            key={`${option.name}-${value}`}
                            type="button"
                            onClick={() => {
                              setSelectedOptions((prev) => ({
                                ...prev,
                                [option.name]: value,
                              }));
                              setSelectedImage(0);
                            }}
                            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                              isActive
                                ? "border-orange-500 bg-orange-50 text-orange-700"
                                : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                            }`}
                          >
                            {value}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Delivered Price Card */}
            <div className="bg-gradient-to-br from-emerald-50 to-green-50 border border-emerald-200 rounded-lg p-6">
              {(() => {
                const currentPricing = shippingSpeed === "express"
                  ? product.pricing?.express
                  : product.pricing?.standard;
                const currentPrice = currentPricing?.final_price || product.landed_price || 0;

                return (
                  <>
                    <div className="flex items-center gap-2 mb-4">
                      <BadgePercent className="h-5 w-5 text-emerald-600" />
                      <span className="text-sm font-semibold text-emerald-700">Guaranteed Delivered Price</span>
                    </div>

                    <div className="bg-white rounded-lg p-4 border-2 border-emerald-400 mb-4">
                      <p className="text-xs text-emerald-600 font-medium mb-1">All-inclusive to {destination}</p>
                      <p className="text-3xl font-bold text-emerald-700" data-testid="product-delivered-price">
                        {formatPrice(currentPrice)}
                      </p>
                      <p className="text-xs text-emerald-600 mt-1">Freight, duties, taxes &amp; clearance included</p>
                    </div>

                    {destination === "BB" && (
                      <div className="flex items-center justify-between bg-white/80 border border-emerald-200 rounded-lg px-4 py-2.5 mb-3">
                        <span className="text-xs font-medium text-slate-700">Customs &amp; clearance included</span>
                        <span className="text-xs font-bold text-emerald-600">Included</span>
                      </div>
                    )}

                    <div className="flex items-start gap-2 bg-emerald-50 border border-emerald-100 rounded-lg px-4 py-2.5">
                      <Info className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-emerald-700 leading-relaxed">
                        100% guaranteed delivered price — no hidden customs calls, no forwarder delays, and direct delivery to your door.
                      </p>
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Price Card */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-6">
              {/* Shipping Speed Selection */}
              <div>
                <p className="text-sm font-medium text-slate-900 mb-3">
                  Delivery Speed
                </p>
                <RadioGroup
                  value={shippingSpeed}
                  onValueChange={setShippingSpeed}
                  className="space-y-2"
                >
                  <div
                    className={`flex items-center justify-between p-3 rounded-sm border cursor-pointer ${
                      shippingSpeed === "standard"
                        ? "border-orange-500 bg-orange-50"
                        : "border-slate-200"
                    }`}
                    onClick={() => setShippingSpeed("standard")}
                  >
                    <div className="flex items-center gap-3">
                      <RadioGroupItem value="standard" id="standard" />
                      <div>
                        <Label htmlFor="standard" className="cursor-pointer flex items-center gap-2">
                          <Truck className="h-4 w-4" />
                          Standard Shipping
                        </Label>
                        <p className="text-xs text-slate-500">7-14 business days</p>
                      </div>
                    </div>
                    <span className="font-medium">{formatPrice(standardPrice)}</span>
                  </div>

                  <div
                    className={`flex items-center justify-between p-3 rounded-sm border cursor-pointer ${
                      shippingSpeed === "express"
                        ? "border-orange-500 bg-orange-50"
                        : "border-slate-200"
                    }`}
                    onClick={() => setShippingSpeed("express")}
                  >
                    <div className="flex items-center gap-3">
                      <RadioGroupItem value="express" id="express" />
                      <div>
                        <Label htmlFor="express" className="cursor-pointer flex items-center gap-2">
                          <Zap className="h-4 w-4" />
                          Express Shipping
                        </Label>
                        <p className="text-xs text-slate-500">3-5 business days</p>
                        <p className="text-[11px] text-orange-600 mt-0.5">
                          Priority clearance &amp; expedited routing
                        </p>
                      </div>
                    </div>
                    <span className="font-medium">{formatPrice(expressPrice)}</span>
                  </div>
                </RadioGroup>
              </div>
            </div>

            {/* Quantity and Add to Cart */}
            <div className="flex items-center gap-4">
              <div className="flex items-center border border-slate-200 rounded-sm">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="px-4 py-2 hover:bg-slate-50"
                  data-testid="quantity-minus"
                >
                  -
                </button>
                <span className="px-4 py-2 border-x border-slate-200 font-medium" data-testid="quantity-value">
                  {quantity}
                </span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="px-4 py-2 hover:bg-slate-50"
                  data-testid="quantity-plus"
                >
                  +
                </button>
              </div>

              <Button
                size="lg"
                className="flex-1 bg-orange-600 hover:bg-orange-700 rounded-sm"
                onClick={handleAddToCart}
                data-testid="add-to-cart-button"
              >
                <ShoppingCart className="h-5 w-5 mr-2" />
                Add to Cart
              </Button>
            </div>

            {/* Trust Badges */}
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-200">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Shield className="h-4 w-4 text-green-600" />
                Secure Checkout
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                Customs Handled
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Package className="h-4 w-4 text-green-600" />
                Tracked Delivery
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Truck className="h-4 w-4 text-green-600" />
                Door-to-Door
              </div>
            </div>

            {/* Source Link */}
            {product.source_url && (
              <a
                href={product.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900"
              >
                <ExternalLink className="h-4 w-4" />
                View on {product.marketplace}
              </a>
            )}
          </div>
        </div>

        {/* Logistics Transparency + Verified Delivery (below product grid, within pt-115px layout) */}
        <div className="mt-10 space-y-6 max-w-7xl">
          <VerifiedDeliveryBadge
            productName={product.name}
            productCategory={product.category}
          />

          <LogisticsTransparency />
        </div>
      </div>
    </NaviCatalogLayout>
  );
}
